import type {
	UploadModel,
	UploadProgressStage,
} from '@sosocio/creator-types/app';
import type {
	UploadErrorKind,
	UploadTransport,
} from './machines';

interface UploadDiagnosticContext {
	failureKind: UploadDiagnosticFailureKind;
	resolvedMime?: UploadModel['_type'];
	retryable: boolean;
	sizeBytes?: number;
	stage: UploadDiagnosticStage;
	transport: UploadTransport;
	uploadId: string;
}

type UploadDiagnosticFailureKind = (
	UploadErrorKind
	| 'preview'
	| 'save'
	| 'unknown'
);

interface UploadDiagnosticMetadata {
	errorCount?: number;
	failureKind: UploadDiagnosticFailureKind;
	hasPhotoModel: boolean;
	hasUploadUrl: boolean;
	height?: number;
	resolvedMime?: UploadModel['_type'];
	retryable: boolean;
	sizeBytes?: number;
	stage: UploadDiagnosticStage;
	transport: UploadTransport;
	uploadId: string;
	width?: number;
}

type UploadDiagnosticStage = (
	UploadProgressStage
	| 'previewing'
	| 'retrying'
);

/**
 * Build an allowlisted Bugsnag metadata snapshot without exposing file names,
 * upload URLs, photo identifiers, timestamps, or future fields added to the
 * upload store model.
 *
 * @param uploadModel Current {@link UploadModel}, when it is still available.
 * @param context Explicit correlation and failure context supplied by the caller.
 * @returns Privacy-safe metadata containing only the documented allowlist.
 */
export function createUploadDiagnosticMetadata(
	uploadModel: UploadModel | undefined,
	context: UploadDiagnosticContext,
): UploadDiagnosticMetadata {
	const metadata: UploadDiagnosticMetadata = {
		failureKind: context.failureKind,
		hasPhotoModel: (
			uploadModel?.photoModelId !== null
			&& typeof uploadModel?.photoModelId !== 'undefined'
		),
		hasUploadUrl: Boolean(uploadModel?.url),
		retryable: context.retryable,
		stage: context.stage,
		transport: context.transport,
		uploadId: context.uploadId,
	};

	if (typeof uploadModel?.errorCount === 'number') {
		metadata.errorCount = uploadModel.errorCount;
	}

	if (typeof uploadModel?.height === 'number') {
		metadata.height = uploadModel.height;
	}

	const resolvedMime = (
		context.resolvedMime
		?? uploadModel?._type
	);

	if (resolvedMime) {
		metadata.resolvedMime = resolvedMime;
	}

	const sizeBytes = (
		context.sizeBytes
		?? uploadModel?.totalBytes
	);

	if (typeof sizeBytes === 'number') {
		metadata.sizeBytes = sizeBytes;
	}

	if (typeof uploadModel?.width === 'number') {
		metadata.width = uploadModel.width;
	}

	return metadata;
}
